<template>
    <div id="orderTicketsGroupEdit" class="relative border-t-2 border-blue-600 mt-4">
        <div v-show="loading" class="absolute z-50 w-full h-full bg-spinner-ghost flex flex-col items-center">
            <spinner class="mb-1"></spinner>
        </div>
        <div class="table-header border-b">
            <div class="flex flex-wrap w-full">
                <span class="font-bold">
                    Existings Order Tickets
                </span>
                <div class="justify-center ml-auto">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0" @click="close">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex trade-tracker-form-save" @click="save">Save</div>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-striped table-hover border border-t-0 order-tickets-table" v-if="tickets.length > 0">
                <thead>
                    <tr>
                        <th class="w-16 text-center">Sort</th>
                        <th @click="sortTable('market_name')">Name<i v-if="sort.field === 'market_name'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th @click="sortTable('long_short')">Order Type<i v-if="sort.field === 'long_short'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th @click="sortTable('entry_date')">Date Filled<i v-if="sort.field === 'entry_date'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th @click="sortTable('exit_date')">Date Exited<i v-if="sort.field === 'exit_date'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th @click="sortTable('profit_loss')">P/L<i v-if="sort.field === 'profit_loss'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th @click="sortTable('ticket_number')">Ticket<i v-if="sort.field === 'ticket_number'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}"></i></th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <draggable v-model="tickets" tag="tbody" handle=".canDrag" item-key="id">
                    <template #item="{ element, index }">
                        <tr>
                            <td class="text-center cursor-move canDrag">
                                <i class="fas fa-arrows-alt text-gray-500"></i>
                            </td>
                            <td>{{ element.market_name || '-' }}</td>
                            <td>{{ element.long_short == 'L' ?  'Long' : 'Short' }}</td>
                            <td>{{ element.entry_date || '-' }}</td>
                            <td>{{ element.exit_date || '-' }}</td>
                            <td>{{ toNumeric(element.profit_loss) }}</td>
                            <td>{{ element.ticket_number || '-' }}</td>
                            <td class="text-center">
                                <a @click="removeTicket(index)" v-tooltip="{ content: 'Delete Current Ticket', placement: 'auto-start', theme: 'red' }"  class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Chart"><i class="fas fa-times"></i></a>
                            </td>
                        </tr>
                    </template>
                </draggable>
                <tfoot>
                    <tr>
                        <th class="w-16 text-center">Sort</th>
                        <th>Name</th>
                        <th>Order Type</th>
                        <th>Date Filled</th>
                        <th>Date Exited</th>
                        <th>P/L</th>
                        <th class="order-tickets-table-center">Ticket</th>
                        <th>Actions</th>
                    </tr>
                </tfoot>
            </table>
            <alert-box class="mb-6" v-else>
                <template #body>
                    There is no data in this order ticket group. Please, save before exit.
                </template>
            </alert-box>
        </div>

        <div class="flex justify-center mt-4">
            <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0" @click="close">Cancel</div>
            <div class="btn btn-sm btn-yellow inline-flex mx-1 trade-tracker-form-save" @click="save">Save</div>
        </div>

        <flash-message :secondsToDisplay="5" ref="flash"></flash-message>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import Draggable from 'vuedraggable';

const props = defineProps({
    initTickets: {
        type: Array,
        required: true
    },
    section: String
});

const flash = ref(null);
const loading = ref(false);
const errors = ref({});
const tickets = ref(JSON.parse(JSON.stringify(props.initTickets)));
const sort = ref({
    field: '',
    direction: 'asc'
});

const hasChanged = computed(() => {
    return JSON.stringify(props.initTickets) !== JSON.stringify(tickets.value);
});

const toNumeric = (number, decimals = 2) => {
    let value = number || 0;
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals).toFixed(decimals);
};

const sortTable = (field) => {
    if (sort.value.field === field) {
        sort.value.direction = sort.value.direction === 'asc' ? 'desc' : 'asc';
    }
    sort.value.field = field;
    tickets.value.sort((a, b) => {
        const aField = a.raw ? a.raw[field] : a[field];
        const bField = b.raw ? b.raw[field] : b[field];
        if (aField < bField) {
            return sort.value.direction === 'asc' ? -1 : 1;
        }
        if (aField > bField) {
            return sort.value.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
};

const removeTicket = (index) => {
    tickets.value.splice(index, 1);
};

const close = () => {
    window.location = window.route('my-folder.order-tickets.index');
};

const save = () => {
    loading.value = true;

    const initialTicketIds = props.initTickets.filter(c => c.id != undefined);
    const ticketIds = tickets.value.filter(c => c.id != undefined);
    const removeTickets =
        [initialTicketIds, ticketIds]
        .sort((a, b) => b.length - a.length)
        .reduce(
            (a, b) => a.filter(
                o => !b.some(v => v.id === o.id)
            )
        )
        .map(x => x.id);

    removeUnloadEvent();

    const route = window.route('my-folder.order-tickets.group-batch-update', { section: props.section });
    axios.post(route, {
        section: props.section,
        tickets: tickets.value,
        removeTickets: removeTickets,
    })
    .then((response) => {
        flash.value.display('Successfully updated «Order Tickets Group»!');
        loading.value = false;
        window.location = window.route('my-folder.order-tickets.index');
    }).catch((error) => {
        errors.value = (error.response.data.errors) ? error.response.data.errors : [];
        if (!error.response.data.message || error.response.data.message == '') {
            flash.value.error('Error updating «Order Tickets», please try again later.');
        } else {
            flash.value.error(error.response.data.message);
        }
        console.error(error);
        loading.value = false;
    });
};

const getEditLink = () => {
    return window.route('my-folder.order-tickets.edit', props.section);
};

const handleLeave = (e) => {
    if (hasChanged.value) {
        e.preventDefault();
        e.returnValue = 'Are you sure?';
        return 'Are you sure?';
    }
};

const removeUnloadEvent = () => {
    window.removeEventListener('beforeunload', handleLeave);
};

onMounted(() => {
    window.addEventListener('beforeunload', handleLeave);
});

onBeforeUnmount(() => {
    removeUnloadEvent();
});
</script>

<style lang="scss">
#orderTicketsGroupEdit {
    .order-tickets-table {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-3 py-2;
                }
            }
            thead {
                tr {
                    @apply border-t;
                    td, th {
                    }
                }
            }
            tbody, tfoot {
                td, th {
                    @apply px-3 text-sm;
                }
            }

            .sortable {
                @apply cursor-pointer;
            }
        }
    }
}
</style>
